<template>
  <login-overlay
    class="login v-login-self-service"
    :inverse="false"
  >
    <totp-login-form
      v-if="totp.required"
      ref="totpLoginForm"
      class="form"
      :errors="totp.errors"
      :pending="isSigningIn"
      :rules="totpRules"
      :totp="totp"
      :username="totp.username"
      @back="onBackToLogin"
      @clearErrors="totp.errors = {}"
      @submit="onTotpSubmit"
    />
    <two-factor-auth-setup-form
      v-else-if="totp.setupRequired"
      class="form"
      :username="totp.username"
      :pending="totp.isSettingUp"
      @submit="onTotpSetupComplete"
      @cancel="onBackToLogin"
    />
    <login-form
      v-else
      ref="loginForm"
      class="form"
      :domain-name="domainName"
      :errors="errors"
      :pending="isSigningIn"
      :rules="rules"
      :session-id="'1'"
      :user="user"
      @clearErrors="errors = {}"
      @submit="onSubmit"
    >
      <el-form-item
        v-if="!domainName"
        :error="errors.domain"
        prop="domain"
      >
        <el-input
          v-model="user.domain"
          autofocus
          auto-complete="on"
          data-testid="domain"
          :disabled="isLoggingIn"
          :label="$t('Domain')"
          name="domain"
          required
          @input="errors = {}"
        />
      </el-form-item>
    </login-form>
  </login-overlay>
</template>

<script>
  import LoginPageMixin from '@/common/mixins/loginPage.mixin';

  import AuthorizationError from '@/common/utils/errors/AuthorizationError';
  import AccessDisabledError from '@/common/utils/errors/AccessDisabledError';
  import ValidationError from '@/common/utils/errors/ValidationError';
  import TotpAuthorizationError from '@/common/utils/errors/TotpAuthorizationError';

  import LoginOverlay from '@/common/components/user/LoginOverlay.vue';
  import LoginForm from '@/common/components/user/LoginForm.vue';
  import TotpLoginForm from '@/common/components/user/TotpLoginForm.vue';
  import TwoFactorAuthSetupForm from '@/self-service/components/user/TwoFactorAuthSetupForm.vue';
  import useAuthStore from '@/common/store/pinia/auth';
  import useTotpStore from '@/common/store/pinia/totp';
  import { mapState, mapStores } from 'pinia';
  import useAppStore from '@/common/store/pinia/app';
  import { totpCodeRule } from '@/common/validation/authRules';

  export default {
    name: 'Login',
    components: {
      LoginOverlay,
      LoginForm,
      TotpLoginForm,
      TwoFactorAuthSetupForm
    },
    mixins: [LoginPageMixin],
    props: {
      domainName: String
    },
    data() {
      return {
        user: {
          domain: '',
          username: '',
          password: ''
        },
        errors: {},
        totp: {
          required: false,
          setupRequired: false,
          receipt: '',
          username: '',
          domain: '',
          password: '',
          code: '',
          errors: {},
          isSettingUp: false
        }
      };
    },
    created() {
      if (this.$route.query.failed) {
        this.onError(new AccessDisabledError());
      }
    },
    computed: {
      ...mapStores(useAuthStore, useAppStore, useTotpStore),
      ...mapState(useAuthStore, ['authReceiptExpired']),
      rules() {
        return {
          domain: { required: true, message: this.$t('Specify a domain') },
          username: { required: true, message: this.$t('Specify a user name') },
          password: { required: true, message: this.$t('Specify a password') },
        };
      },
      totpRules() {
        return {
          code: totpCodeRule()
        };
      }
    },
    watch: {
      domainName: {
        immediate: true,
        handler(newVal) {
          if (!this.user.domain && newVal) {
            this.user.domain = newVal;
          }
        }
      },
      authReceiptExpired: {
        handler(expired) {
          if (expired && (this.totp.required || this.totp.setupRequired)) {
            this.onBackToLogin();
            this.errors = {
              username: true,
              password: true,
              backend: this.$t('Two-factor authentication has timed out. Please log in again.')
            };
          }
        }
      }
    },
    methods: {
      onSubmit() {
        this.errors = {};

        this.$refs.loginForm.validate((valid) => {
          if (valid) {
            return this.authStore.signInWithDomain({
              domain: this.user.domain.trim(),
              username: this.user.username.trim(),
              password: this.user.password,
              domainAdminStartPageEnabled: false
            })
              .catch(this.onError);
          }
          return Promise.resolve();
        });
      },
      onTotpSubmit() {
        this.totp.errors = {};
        this.$refs.totpLoginForm.validate((valid) => {
          if (valid) {
            this.authStore.signInTotpWithDomain(
              this.totp.domain,
              this.totp.username,
              this.totp.password,
              this.totp.code,
              this.totp.receipt
            ).catch(this.onTotpError);
          }
        });
      },
      onBackToLogin() {
        this.totp.required = false;
        this.totp.setupRequired = false;
        this.totp.receipt = '';
        this.totp.username = '';
        this.totp.domain = '';
        this.totp.password = '';
        this.totp.code = '';
        this.totp.errors = {};
        this.user.password = null;
      },
      async onTotpSetupComplete(code) {
        this.totp.isSettingUp = true;
        try {
          await this.totpStore.enableTotp(code, this.totp.receipt);
          this.totp.setupRequired = false;

          await this.authStore.signInTotpWithDomain(
            this.totp.domain,
            this.totp.username,
            this.totp.password,
            code,
            this.totp.receipt
          );
        } finally {
          this.totp.isSettingUp = false;
        }
      },
      getLandingPage() {
        return this.appStore.fetchSsLandingPage();
      },
      onError(err) {
        this.sendPostMessage('authFailed');

        if (err.fields) {
          this.errors = err.fields;
        } else if (err instanceof AccessDisabledError) {
          this.errors = {
            domain: true,
            backend: this.$t('Login has been disabled by the administrator')
          };
        } else if (err instanceof TotpAuthorizationError) {
          this.handleTotpAuthorizationError(err);
        } else if (err instanceof AuthorizationError) {
          this.errors = {
            username: true,
            password: true,
            backend: this.$t('Incorrect login or password')
          };
        } else {
          this.$router.push({ name: 'errorServiceDisabled' });
        }
      },
      handleTotpAuthorizationError(err) {
        this.totp.username = this.user.username;
        this.totp.domain = this.user.domain;
        this.totp.receipt = err.totpReceipt || '';

        if (err.isSetupRequired) {
          this.totp.setupRequired = true;
        } else if (err.totpReceipt) {
          this.totp.password = this.user.password;
          this.totp.required = true;
        }
      },
      onTotpError(err) {
        this.sendPostMessage('authFailed');
        if (err instanceof ValidationError || err instanceof AuthorizationError || err instanceof AccessDisabledError) {
          this.totp.errors = {
            code: true,
            backend: this.$t('The entered code is incorrect or outdated')
          };
        } else {
          this.$router.push({ name: 'errorServiceDisabled' });
        }
      }
    }
  };
</script>
<style lang="scss" scoped>
  .login {
    background: $av-brand-lightest;
  }

  .form {
    box-shadow: $av-box-shadow rgba(0, 0, 0, 0.25);
  }
</style>
